import Badge from "@material-ui/core/Badge";
import ListItem, { ListItemProps } from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import createStyles from "@material-ui/core/styles/createStyles";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { SvgIconProps } from "@material-ui/core/SvgIcon";
import Tooltip from "@material-ui/core/Tooltip";
import clsx from "clsx";
import React from "react";
import { Color } from "../../theme";
import { HasTestId, PlainFunctionComponent } from "../../types";
import { ifDefined, isUndefined } from "../../util";

// NOTE: Only for internal use such that not exported via `./index.ts`

// Justification: false positive because `{}` is not used as type here
// eslint-disable-next-line @typescript-eslint/ban-types
export type NavigationListItemProps<ComponentT extends React.ElementType = "div", PropsT extends { button?: true; drawerExpanded?: boolean } & HasTestId = {}> = ListItemProps<
    ComponentT,
    PropsT & { drawerExpanded?: boolean } & HasTestId
>;

// Justification: false positive because `{}` is not used as type here
// eslint-disable-next-line @typescript-eslint/ban-types
export const NavigationListItem = <ComponentT extends React.ElementType = "div", PropsT extends { button?: true; drawerExpanded?: boolean } & HasTestId = {}>({
    children,
    className,
    drawerExpanded,
    testId,
    ...props
}: NavigationListItemProps<ComponentT, PropsT>): React.ReactElement<NavigationListItemProps<ComponentT, PropsT>, string> => {
    const expanded = drawerExpanded ?? true;

    const classes = useStyles();
    return (
        <ListItem button={true} className={clsx(classes.listItem, { [classes.listItemCollapsed]: !expanded }, className)} data-testid={testId} {...props}>
            {children}
        </ListItem>
    );
};

export interface NavigationListItemContentProps {
    badgeClassName?: string;
    badgeCounter?: number | boolean;
    drawerExpanded?: boolean;
    Icon?: React.ElementType<SvgIconProps>;
    label: NonNullable<React.ReactNode>;
    selected?: boolean;
}

export const NavigationListItemContent: PlainFunctionComponent<NavigationListItemContentProps> = ({ badgeClassName, badgeCounter, drawerExpanded, Icon, label, selected }) => {
    const expanded = isUndefined(drawerExpanded) || drawerExpanded;

    const classes = useStyles();
    const listItemIconAndTextClasses = clsx(classes.listItemText, { [classes.listItemSelected]: selected });
    const icon = ifDefined(Icon, (I) => <I className={listItemIconAndTextClasses} />);
    return (
        <>
            {ifDefined(icon, (i) => (
                <ListItemIcon className={classes.listItemIcon}>
                    {expanded ? (
                        i
                    ) : (
                        <Tooltip placement="right" title={label}>
                            {i}
                        </Tooltip>
                    )}
                </ListItemIcon>
            ))}
            <ListItemText primaryTypographyProps={{ noWrap: true }}>
                <Badge
                    anchorOrigin={{ horizontal: "right", vertical: "top" }}
                    badgeContent={ifDefined(badgeCounter, (counter) => (typeof counter === "number" ? counter : undefined))}
                    classes={{
                        anchorOriginTopRightRectangle: classes.listItemBadgeDotAnchor,
                        badge: classes.listItemBadge,
                        invisible: classes.listItemBadgeInvisible,
                    }}
                    className={clsx(badgeClassName, listItemIconAndTextClasses)}
                    overlap="rectangle"
                    variant={ifDefined(badgeCounter, (counter) => (typeof counter === "number" ? "standard" : counter ? "dot" : undefined))}
                >
                    {label}
                </Badge>
            </ListItemText>
        </>
    );
};

const useStyles = makeStyles(
    createStyles({
        listItem: {
            padding: "10px 24px", // padding 10px because of missing Icon
            "& > svg": {
                color: Color.grey5,
                padding: 0,
                marginRight: -4, // counteract the icons "padding" on the right side (edge="end" is not possible for this use case)
            },
            "&:focus, &:hover": {
                backgroundColor: Color.focus5,
            },
            "&:focus > svg, &:hover > svg": {
                color: Color.blue1,
            },
        },
        listItemCollapsed: {
            padding: "10px 18px 10px 14px",
            "& > .MuiListItemIcon-root": {
                minWidth: 34, // compensate above 14px `padding-right` with 2px more minimal `width`
            },
        },
        listItemBadge: {
            // bump up specificity as otherwise it depends on import order in using application
            "&&": {
                backgroundColor: Color.blue1,
                color: Color.white,
                position: "static", // `unset` not supported in IE
            },
        },
        listItemBadgeDotAnchor: {
            // bump up specificity as otherwise it depends on import order in using application
            "&&": {
                margin: "auto 8px",
                transform: "none", // no default transform, we use `margin` here
            },
        },
        listItemBadgeInvisible: {
            // bump up specificity as otherwise it depends on import order in using application
            "&&": {
                display: "none", // originally done by moving out of sight using `transform` which does not work due to bumped specificity
            },
        },
        listItemIcon: {
            minWidth: 32,
        },
        listItemText: {
            color: Color.blue1,
            fontWeight: "bold",
            lineHeight: "1em", // override line-height as otherwise `20px` with badges lead to 21.1167px box height (even if not 100% clear why)
            textAlign: "center",
        },
        // Last position because highest precedence
        listItemSelected: {
            color: Color.grey5,
        },
    }),
);
